import React from 'react';
import { Field, useFormikContext } from 'formik';
import { Typography, FormControl, FormHelperText } from '@mui/material';
import {
    StepperControls,
    TextField,
    StepWrapper,
    StyledCardSection,
    ComboBox,
    YesNoSelector,
} from '@fnol/library/src/components';
import { common, maxCharacterLimit } from '@fnol/library/src/lib';
import { requestType, translations, question50MaxLength } from '../../lib/consts';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
    values: GenericObject[];
};

const ClaimDetailsScreen = ({ navigator }: Props) => {
    const { values, setTouched, setErrors, touched, errors, setFieldValue, handleChange } = useFormikContext<any>();

    const [currentStep, { goForward, goBack }] = navigator;

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                euReport: true,
                policeInspection: true,
                policeOnScene: true,
                howDidDamageOccur: true,
                reportedToPolice: true,
                cityInSerbiaClaim: true,
            },
            false,
        );
        const existingErrors: GenericObject = {};

        if (!values.cityInSerbiaClaim || values.cityInSerbiaClaim.id === '') {
            existingErrors.cityInSerbiaClaim = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.euReport === '') {
            existingErrors.euReport = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.reportedToPolice === '') {
            existingErrors.reportedToPolice = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.reportedToPolice === 'true' && values.euReport === 'false' && values.policeInspection === '') {
            existingErrors.policeInspection = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.reportedToPolice === 'true' && values.euReport === 'true' && values.policeOnScene === '') {
            existingErrors.policeOnScene = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.howDidDamageOccur === '') {
            existingErrors.howDidDamageOccur = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        setErrors(existingErrors);

        if (valid) {
            goForward();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const handleChangeEuReport = () => {
        setFieldValue('policeInspection', '');
        setFieldValue('reportedToPolice', '');
        setFieldValue('policeOnScene', '');
    };

    const handleChangeReportedToPolice = () => {
        setFieldValue('policeInspection', '');
        setFieldValue('policeOnScene', '');
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.claimDetail.PLACE_OF_OCCURANCE}
                </Typography>
                <FormControl error={!!(touched.cityInSerbiaClaim && errors.cityInSerbiaClaim)}>
                    <Field
                        name="cityInSerbiaClaim"
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values.cityInSerbiaClaim}
                        as={ComboBox}
                        error={!!(touched.cityInSerbiaClaim && errors.cityInSerbiaClaim)}
                    />
                    {touched.cityInSerbiaClaim && errors.cityInSerbiaClaim ? (
                        <FormHelperText error={true}>
                            <>{errors.cityInSerbiaClaim}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                <FormControl>
                    <Field
                        name="streetClaim"
                        label={translations.claimDetail.STREET}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                    />
                </FormControl>
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.claimDetail.CIRCUMSTANCES_OF_DAMAGE}
                </Typography>
                <Typography variant="subtitle1">{translations.claimDetail.EU_TRAFFIC_ACCIDENT}</Typography>
                <FormControl>
                    <YesNoSelector name="euReport" onChange={handleChangeEuReport} />
                </FormControl>
                <Typography variant="subtitle1">{translations.claimDetail.WAS_POLICE_NOTIFIED}</Typography>
                <FormControl>
                    <YesNoSelector name="reportedToPolice" onChange={handleChangeReportedToPolice} />
                </FormControl>
                {values.reportedToPolice === 'true' && values.euReport === 'true' && (
                    <>
                        <Typography variant="subtitle1">{translations.claimDetail.POLICE_ON_SCENE}</Typography>
                        <FormControl>
                            <YesNoSelector name="policeOnScene" />
                        </FormControl>
                    </>
                )}
                {values.reportedToPolice === 'true' && values.euReport === 'false' && (
                    <>
                        <Typography variant="subtitle1">{translations.claimDetail.POLICE_INVESTIGATION}</Typography>
                        <FormControl>
                            <YesNoSelector name="policeInspection" />
                        </FormControl>
                    </>
                )}
                <FormControl>
                    <Field
                        name="howDidDamageOccur"
                        label={translations.claimDetail.DESCRIBE_EVENT}
                        multiline={true}
                        as={TextField}
                        maxRows={5}
                        minRows={5}
                        inputProps={{
                            maxLength: maxCharacterLimit,
                        }}
                        value={values.howDidDamageOccur}
                        helperText={`${translations.REMAINING_CHARACTERS} ${
                            maxCharacterLimit - values.howDidDamageOccur.length
                        }`}
                        error={!!(touched.howDidDamageOccur && errors.howDidDamageOccur)}
                    />
                    {touched.howDidDamageOccur && errors.howDidDamageOccur ? (
                        <FormHelperText error={true}>
                            <>{errors.howDidDamageOccur}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            <StepperControls onPrevious={handleGoBack} onNext={handleNext} onNextLabel={common.NEXT} />
        </StepWrapper>
    );
};

export default ClaimDetailsScreen;
